import { loadStripe } from "@stripe/stripe-js";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";

import AiIcon from "@/assets/all-icons/ai-icon";
import ArtlistIcon from "@/assets/all-icons/artlist-icon";
import ArtworkIcon from "@/assets/all-icons/artwork-icon";
import BasquiatCrownIcon from "@/assets/all-icons/basquiat-crown-icon";
import BoardIcon from "@/assets/all-icons/board-icon";
import CreditCardIcon from "@/assets/all-icons/credit-card-icon";
import GalleryIcon from "@/assets/all-icons/gallery-icon";
import GamesIcon from "@/assets/all-icons/games-icon";
import stripeConfig, { resolveStripePublishableKey } from "@/config/stripe.config";
import usePaymentProfile from "@/hooks/usePaymentProfile";
import useStateHandler from "@/hooks/useStateHandler";
import paymentProfileService from "@/services/paymentProfile.service";
import suartePlusService from "@/services/suartePlus.service";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import XIcon from "@/shared-components/icons/components/public/XIcon";
import BillingDetailsCard from "@/shared-components/payment-profile/BillingDetailsCard";
import GenericPopup from "@/shared-components/popups/components/GenericPopup";
import { formatCurrencyAmount } from "@/utils/currency.utils";

import "./styles/SuartePlusPopup.css";

const DEFAULT_BENEFITS = [
  {
    id: "artwork_ai",
    label: "Unlimited access to Artwork AI",
  },
  {
    id: "posts",
    label: "Create and share posts with the community",
  },
  {
    id: "ask_ai",
    label: "Access to Profile AI features",
  },
  {
    id: "artlists",
    label: "Create unlimited artlists",
  },
  {
    id: "game_saves",
    label: "Play exclusive art games with unlimited saves",
  },
  {
    id: "insignia",
    label: "Receive the Suarte Plus insignia",
  },
  {
    id: "limited_edition_collectors",
    label: "View LEs collectors and sold quantities",
  },
  {
    id: "gallery_messages",
    label: "Message galleries directly",
  },
  {
    id: "offers",
    label: "Access exclusive offers and promotions",
  },
];

const BENEFIT_ICONS = {
  artwork_ai: ArtworkIcon,
  limited_edition_collectors: ArtworkIcon,
  ask_ai: AiIcon,
  artlists: ArtlistIcon,
  game_saves: GamesIcon,
  insignia: BasquiatCrownIcon,
  posts: BoardIcon,
  gallery_messages: GalleryIcon,
  offers: CreditCardIcon,
};
const DEFAULT_PLAN = "monthly";
const YEARLY_PLAN_KEY = "yearly";
const YEARLY_DISCOUNT_PERCENTAGE = 10;
const PAYMENT_INTENT_NOTE = "Stripe's payment intent has to succeed for it to show, and automatically collect the money.";
const SUARTE_PLUS_FALLBACK_MONTHLY_EUR = 8;

function pickArray(payload) {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.data)) {
    return payload.data;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  if (Array.isArray(payload.payment_methods)) {
    return payload.payment_methods;
  }
  if (Array.isArray(payload.items)) {
    return payload.items;
  }
  return [];
}

function normalizePaymentMethod(candidate, fallbackDefault) {
  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const cardDetails =
    candidate.card ||
    candidate.payment_method_details?.card ||
    candidate.payment_method?.card ||
    candidate.details?.card ||
    candidate.billing_details?.card ||
    null;

  const id =
    candidate.id ||
    candidate.payment_method_id ||
    candidate.payment_method ||
    candidate.stripe_payment_method_id ||
    candidate._id ||
    null;

  if (!id) {
    return null;
  }

  const brand = cardDetails?.brand ?? candidate.brand ?? candidate.card_brand ?? "Card";
  const last4 = String(cardDetails?.last4 ?? candidate.last4 ?? candidate.card_last4 ?? "").slice(-4);

  return {
    id,
    label: candidate.nickname ?? candidate.label ?? brand,
    details: candidate.details ?? null,
    last4,
    brand,
    isDefault:
      Boolean(candidate.is_default ?? candidate.isDefault ?? candidate.default) ||
      (fallbackDefault ? id === fallbackDefault : false),
  };
}

function normalizePaymentMethodsPayload(payload) {
  const defaultId =
    payload?.default_payment_method_id ??
    payload?.default_payment_method ??
    payload?.default ??
    payload?.defaultPaymentMethodId ??
    null;

  const normalized = pickArray(payload)
    .map((item) => normalizePaymentMethod(item, defaultId))
    .filter(Boolean);

  const resolvedDefaultId = normalized.find((item) => item.isDefault)?.id ?? defaultId ?? null;

  const items = normalized
    .map((item) => ({
      ...item,
      isDefault: resolvedDefaultId ? item.id === resolvedDefaultId : item.isDefault,
    }))
    .sort((a, b) => {
      if (a.isDefault === b.isDefault) {
        return 0;
      }
      return a.isDefault ? -1 : 1;
    });

  return { items, defaultId: resolvedDefaultId };
}

export default function SuartePlusPopup({
  open,
  onClose,
  actionTitle,
  benefits = DEFAULT_BENEFITS,
  highlightedBenefit,
  onSuccess,
  ctaLabel = "Join Suarte Plus",
}) {
  const paymentProfile = usePaymentProfile();
  const ensurePaymentProfile = paymentProfile?.ensurePaymentProfile;
  const paymentProfileCountry =
    paymentProfile?.profile?.country ??
    paymentProfile?.profile?.country_code ??
    paymentProfile?.profile?.country_iso ??
    paymentProfile?.profile?.country_iso2 ??
    null;
  const bidderBillingAddress = paymentProfile?.profile?.billing_address ?? null;
  const bidderBillingInfo = paymentProfile?.profile?.billing_info ?? null;
  const { stateHandler } = useStateHandler();

  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(DEFAULT_PLAN);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [showAllBenefits, setShowAllBenefits] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [paymentMethodsError, setPaymentMethodsError] = useState(null);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [paymentMethodsVersion, setPaymentMethodsVersion] = useState(0);
  const [addingPaymentMethod, setAddingPaymentMethod] = useState(false);
  const [processingSubscription, setProcessingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [vatSummary, setVatSummary] = useState(null);
  const [vatSummaryError, setVatSummaryError] = useState(null);
  const [vatSummaryLoading, setVatSummaryLoading] = useState(false);
  const [stripePromise, setStripePromise] = useState(() =>
    stripeConfig.publishableKey ? loadStripe(stripeConfig.publishableKey) : null,
  );
  const [billingAddress, setBillingAddress] = useState(() => bidderBillingAddress);
  const [billingInfo, setBillingInfo] = useState(() => bidderBillingInfo);

  const normalizedBenefits = useMemo(() => {
    const source = Array.isArray(benefits) && benefits.length > 0 ? benefits : DEFAULT_BENEFITS;
    const withStableIds = source.map((benefit, index) => ({
      ...benefit,
      id: benefit.id ?? DEFAULT_BENEFITS[index]?.id ?? `benefit_${index}`,
    }));
    const highlightedIndex = withStableIds.findIndex((benefit) => benefit.id === highlightedBenefit);

    if (highlightedIndex <= 0) {
      return withStableIds;
    }

    const highlighted = withStableIds[highlightedIndex];
    return [highlighted, ...withStableIds.filter((_, index) => index !== highlightedIndex)];
  }, [benefits, highlightedBenefit]);

  const selectedPaymentMethod = useMemo(() =>
    paymentMethods.find((method) => method.id === selectedPaymentMethodId) ?? null,
  [paymentMethods, selectedPaymentMethodId]
  );
  const buyerCountryHint = useMemo(() => {
    const fromBilling = billingAddress?.address_country;
    if (typeof fromBilling === "string" && fromBilling.trim()) {
      return fromBilling.trim();
    }
    if (typeof paymentProfileCountry === "string" && paymentProfileCountry.trim()) {
      return paymentProfileCountry.trim();
    }
    return null;
  }, [billingAddress?.address_country, paymentProfileCountry]);
  const billingVatFingerprint = useMemo(() => ([
    billingInfo?.is_business === true ? "1" : "0",
    typeof billingInfo?.vat_id === "string" ? billingInfo.vat_id.trim().toUpperCase() : "",
    billingInfo?.vat_valid === true ? "1" : "0",
  ].join("|")), [billingInfo?.is_business, billingInfo?.vat_id, billingInfo?.vat_valid]);
  const rawPlanOptions = useMemo(() => {
    if (!config?.plans) {
      return [];
    }
    return Object.entries(config.plans).map(([key, details]) => {
      const cycleMonths = Number(details.cycleMonths ?? (key === YEARLY_PLAN_KEY ? 12 : 1));
      const originalAmount = Number(details.amount ?? 0);
      const hasYearlyDiscount = key === YEARLY_PLAN_KEY || cycleMonths >= 12;
      const discountPercentage = hasYearlyDiscount ? YEARLY_DISCOUNT_PERCENTAGE : 0;
      const discountMultiplier = hasYearlyDiscount ? 1 - discountPercentage / 100 : 1;
      const amount = Number((originalAmount * discountMultiplier).toFixed(2));
      return {
        key,
        label: details.label ?? key,
        amount,
        originalAmount,
        discountPercentage,
        hasDiscount: hasYearlyDiscount && originalAmount > amount,
        cycleMonths,
        period: cycleMonths >= 12 ? "per year" : "per month",
      };
    });
  }, [config]);

  const planOptions = useMemo(() => {
    if (rawPlanOptions.length === 0) {
      return [];
    }
    const normalized = [...rawPlanOptions];
    const hasYearlyPlan = normalized.some((plan) => plan.key === YEARLY_PLAN_KEY);
    if (!hasYearlyPlan) {
      const referencePlan = normalized.find((plan) => plan.key === DEFAULT_PLAN) ?? normalized[0];
      if (referencePlan) {
        const yearlyOriginal = Number((referencePlan.originalAmount * 12).toFixed(2));
        const yearlyAmount = Number(
          (yearlyOriginal * (1 - YEARLY_DISCOUNT_PERCENTAGE / 100)).toFixed(2),
        );
        normalized.push({
          key: YEARLY_PLAN_KEY,
          label: "yearly",
          amount: yearlyAmount,
          originalAmount: yearlyOriginal,
          discountPercentage: YEARLY_DISCOUNT_PERCENTAGE,
          hasDiscount: yearlyOriginal > yearlyAmount,
          cycleMonths: 12,
          period: "per year",
        });
      }
    }
    return normalized.sort((a, b) => {
      if (a.cycleMonths === b.cycleMonths) {
        return a.key.localeCompare(b.key);
      }
      return a.cycleMonths - b.cycleMonths;
    });
  }, [rawPlanOptions]);

  const selectedPlanDetails = useMemo(() =>
    planOptions.find((option) => option.key === selectedPlan) ?? planOptions[0] ?? null,
  [planOptions, selectedPlan]
  );

  const planAmount = selectedPlanDetails?.amount ?? 0;
  const vatRate = Number(config?.vatPercentage ?? 0);
  const formatAmount = useCallback((value, digits = 2) => formatCurrencyAmount(value, config?.currency, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }) || "", [config?.currency]);
  const fallbackVatAmount = Number((planAmount * vatRate).toFixed(2));
  const resolvedVatAmount = Number.isFinite(vatSummary?.vatAmount) ? vatSummary.vatAmount : fallbackVatAmount;
  const resolvedVatRate = Number.isFinite(vatSummary?.vatRate) ? vatSummary.vatRate : vatRate;
  const resolvedTotal = Number.isFinite(vatSummary?.total)
    ? vatSummary.total
    : Number((planAmount + resolvedVatAmount).toFixed(2));
  const vatRateLabel = Number.isFinite(resolvedVatRate)
    ? `${Math.round(resolvedVatRate * 100)}%`
    : "--";
  const checkoutPriceLabel = formatAmount(resolvedTotal, 2);

  const planSelectionTitle = actionTitle || "Suarte Plus";

  const resetState = useCallback(() => {
    setShowPaymentDetails(false);
    setShowAllBenefits(false);
    setPaymentMethods([]);
    setSelectedPaymentMethodId(null);
    setPaymentMethodsError(null);
    setSubscriptionError(null);
    setLoadingPaymentMethods(false);
    setAddingPaymentMethod(false);
    setProcessingSubscription(false);
  }, []);

  const ensureStripeInstance = useCallback(async () => {
    if (stripePromise) {
      return stripePromise;
    }
    const publishableKey = await resolveStripePublishableKey();
    if (!publishableKey) {
      return null;
    }
    const promise = loadStripe(publishableKey);
    setStripePromise(promise);
    return promise;
  }, [stripePromise]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    setConfigError(null);
    suartePlusService.getConfig()
      .then(({ response, data }) => {
        if (!response?.ok) {
          const message = data?.message ?? "Unable to load Suarte Plus plans.";
          throw new Error(message);
        }
        setConfig(data);
      })
      .catch((error) => {
        setConfigError(error?.message ?? "Unable to load Suarte Plus plans.");
      });
  }, [open, resetState]);

  useEffect(() => {
    if (open) {
      setBillingAddress(bidderBillingAddress ?? null);
      setBillingInfo(bidderBillingInfo ?? null);
    }
  }, [bidderBillingAddress, bidderBillingInfo, open]);

  useEffect(() => {
    if (config?.plans && !config.plans[selectedPlan]) {
      const fallback = Object.keys(config.plans)[0] ?? DEFAULT_PLAN;
      setSelectedPlan(fallback);
    }
  }, [config, selectedPlan]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setLoadingPaymentMethods(true);
    setPaymentMethodsError(null);

    paymentProfileService.listPaymentMethods()
      .then(({ response, data }) => {
        if (!open) {
          return;
        }
        if (response?.ok) {
          const normalized = normalizePaymentMethodsPayload(data);
          setPaymentMethods(normalized.items);
          if (normalized.items.length > 0) {
            setSelectedPaymentMethodId((prev) => prev || normalized.items[0]?.id);
          }
          return;
        }
        if (response?.status === 404) {
          setPaymentMethods([]);
          return;
        }
        throw new Error(data?.message ?? data?.error_message ?? "Unable to load payment methods.");
      })
      .catch((error) => {
        setPaymentMethodsError(error?.message ?? "Unable to load payment methods.");
        setPaymentMethods([]);
      })
      .finally(() => {
        if (open) {
          setLoadingPaymentMethods(false);
        }
      });
  }, [open, paymentMethodsVersion]);

  useEffect(() => {
    if (!open || !showPaymentDetails || !Number.isFinite(planAmount) || planAmount <= 0) {
      setVatSummary(null);
      setVatSummaryError(null);
      setVatSummaryLoading(false);
      return;
    }

    let active = true;
    setVatSummaryLoading(true);
    setVatSummaryError(null);

    suartePlusService.calculateVat({
      amount: planAmount,
      payment_method_id: selectedPaymentMethodId ?? null,
      buyer_country: buyerCountryHint ?? null,
    })
      .then(({ response, data }) => {
        if (!active) {
          return;
        }
        if (!response?.ok) {
          setVatSummary(null);
          setVatSummaryError(data?.message ?? "Unable to calculate VAT.");
          return;
        }
        setVatSummary(data ?? null);
      })
      .catch((error) => {
        if (active) {
          setVatSummary(null);
          setVatSummaryError(error?.message ?? "Unable to calculate VAT.");
        }
      })
      .finally(() => {
        if (active) {
          setVatSummaryLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [billingVatFingerprint, buyerCountryHint, open, planAmount, selectedPaymentMethodId, showPaymentDetails]);

  const handleAddPaymentMethod = useCallback(async () => {
    if (typeof ensurePaymentProfile !== "function") {
      console.warn("[SuartePlusPopup] ensurePaymentProfile missing");
      return;
    }

    setAddingPaymentMethod(true);
    try {
      await ensurePaymentProfile({
        forceSetup: true,
        countryHint: paymentProfileCountry ?? undefined,
      });
      setPaymentMethodsVersion((value) => value + 1);
      stateHandler?.set("temporalPopup", {
        text: "Payment method added successfully!",
      });
    }
    catch (error) {
      if (error?.code !== "BIDDER_PROFILE_CANCELLED") {
        console.error("[SuartePlusPopup] Unable to add payment method", error);
        stateHandler?.set("temporalPopup", {
          text: "Unable to add a payment method right now.",
        });
      }
    }
    finally {
      setAddingPaymentMethod(false);
    }
  }, [ensurePaymentProfile, paymentProfileCountry, stateHandler]);

  const handleRequiresAction = useCallback(async ({ clientSecret, stripeSubscriptionId }) => {
    if (!clientSecret || !stripeSubscriptionId) {
      throw new Error("Unable to complete payment confirmation.");
    }
    const stripeInstance = await ensureStripeInstance();
    if (!stripeInstance) {
      throw new Error("Stripe is unavailable. Please try again in a moment.");
    }

    console.info("[SuartePlusPopup] confirming card payment because Stripe requested additional authentication", {
      stripeSubscriptionId,
      plan: selectedPlan,
    });

    const confirmResult = await stripeInstance.confirmCardPayment(clientSecret);
    if (confirmResult?.error) {
      console.error("[SuartePlusPopup] confirmCardPayment failed", confirmResult.error);
      throw confirmResult.error;
    }

    console.info("[SuartePlusPopup] confirmCardPayment succeeded, finalizing subscription", {
      stripeSubscriptionId,
      paymentIntentStatus: confirmResult?.paymentIntent?.status,
    });

    const finalizeResult = await suartePlusService.confirmPayment({
      plan: selectedPlan,
      stripe_subscription_id: stripeSubscriptionId,
      payment_method_id: selectedPaymentMethodId ?? null,
    });

    if (!finalizeResult?.response?.ok) {
      const message = finalizeResult?.data?.message ?? finalizeResult?.data?.error_message ?? "Unable to finalize Suarte Plus subscription.";
      console.error("[SuartePlusPopup] confirmPayment failed", {
        responseStatus: finalizeResult?.response?.status,
        message,
        payload: finalizeResult?.data,
      });
      throw new Error(message);
    }

    stateHandler?.set("temporalPopup", {
      text: "Suarte Plus is now active!",
    });
    onSuccess?.(finalizeResult.data);
    onClose?.();
  }, [ensureStripeInstance, onClose, onSuccess, selectedPaymentMethodId, selectedPlan, stateHandler]);

  const handlePrimaryAction = useCallback(async () => {
    if (processingSubscription) {
      return;
    }
    if (!showPaymentDetails) {
      setShowPaymentDetails(true);
      return;
    }
    if (!selectedPaymentMethod) {
      setSubscriptionError("Select a payment method to continue.");
      return;
    }

    setProcessingSubscription(true);
    setSubscriptionError(null);

    try {
      const { response, data } = await suartePlusService.subscribe({
        plan: selectedPlan,
        payment_method_id: selectedPaymentMethod.id,
        buyer_country: buyerCountryHint ?? null,
      });

      console.info("[SuartePlusPopup] subscribe response", {
        status: response?.status,
        ok: response?.ok,
        payload: data,
      });

      if (!response?.ok) {
        throw new Error(data?.message ?? data?.error_message ?? "Unable to activate Suarte Plus.");
      }

      if (response.status === 202 && data?.status === "requires_action") {
        await handleRequiresAction({
          clientSecret: data?.client_secret,
          stripeSubscriptionId: data?.stripe_subscription_id,
        });
        return;
      }

      console.info("[SuartePlusPopup] Suarte Plus activated without extra authentication", {
        plan: selectedPlan,
      });

      stateHandler?.set("temporalPopup", {
        text: "Suarte Plus is now active!",
      });
      onSuccess?.(data);
      onClose?.();
    }
    catch (error) {
      console.error("[SuartePlusPopup] subscription failed", error);
      setSubscriptionError(error?.message ?? "Unable to activate Suarte Plus. Please try again.");
    }
    finally {
      setProcessingSubscription(false);
    }
  }, [buyerCountryHint, handleRequiresAction, onClose, onSuccess, processingSubscription, selectedPaymentMethod, selectedPlan, showPaymentDetails, stateHandler]);

  function handleClose() {
    resetState();
    onClose?.();
  }

  const normalizedBenefitsList = normalizedBenefits.map((benefit, index) => {
    const IconComponent = BENEFIT_ICONS[benefit.id] || ArtlistIcon;
    const label = benefit.label ?? benefit.title;
    return (
      <li
        className={`suarte-plus-popup__benefit${index === 0 && benefit.id === highlightedBenefit ? " suarte-plus-popup__benefit--highlighted" : ""}`}
        key={benefit.id ?? `${label}-${index}`}
      >
        <span className="suarte-plus-popup__benefit-icon" aria-hidden="true">
          <IconComponent className="suarte-plus-popup__benefit-icon-svg" />
        </span>
        <span className="suarte-plus-popup__benefit-label">{label}</span>
      </li>
    );
  });
  const previewBenefitsList = normalizedBenefitsList.slice(0, 3);
  const hasAdditionalBenefits = normalizedBenefitsList.length > previewBenefitsList.length;

  const planSelection = (
    <section className="suarte-plus-popup__plan-section">
      <p className="suarte-plus-popup__plan-heading">Choose your plan</p>
      <div className="suarte-plus-popup__plan-options">
        {(planOptions.length
          ? planOptions
          : [
            {
              key: DEFAULT_PLAN,
              label: "monthly",
              amount: SUARTE_PLUS_FALLBACK_MONTHLY_EUR,
              originalAmount: SUARTE_PLUS_FALLBACK_MONTHLY_EUR,
              discountPercentage: 0,
              hasDiscount: false,
              period: "per month",
            },
          ]
        ).map((planOption) => {
          const isActive = planOption.key === selectedPlan;
          const showDiscount = Boolean(planOption.hasDiscount);
          return (
            <button
              key={planOption.key}
              type="button"
              className={`suarte-plus-popup__plan-option${isActive ? " suarte-plus-popup__plan-option--active" : ""}`}
              onClick={() => setSelectedPlan(planOption.key)}
              aria-pressed={isActive}
            >
              <div className="suarte-plus-popup__plan-label-row">
                <span className="suarte-plus-popup__plan-label">{planOption.label}</span>
                {showDiscount && (
                  <span className="suarte-plus-popup__plan-discount-badge">
                    Save {planOption.discountPercentage}%
                  </span>
                )}
              </div>
              <div className="suarte-plus-popup__plan-price">
                {showDiscount && (
                  <span className="suarte-plus-popup__plan-original">
                    {formatAmount(planOption.originalAmount, 2)}
                  </span>
                )}
                <strong className="suarte-plus-popup__plan-amount">
                  {formatAmount(planOption.amount, 2)}
                </strong>
              </div>
            </button>
          );
        })}
      </div>
      <p className="suarte-plus-popup__plan-note">VAT not included</p>
    </section>
  );

  return (
    <>
      <GenericPopup
        open={open}
        className="suarte-plus-popup__card"
        backgroundClassName="suarte-plus-popup__backdrop"
        onBackgroundClick={handleClose}
      >
        <div className="suarte-plus-popup__content">
          <div>
            <header className="suarte-plus-popup__header element-non-selectable">
              <h2 className="suarte-plus-popup__title">{planSelectionTitle}</h2>
              <XIcon className="suarte-plus-popup__close" onClick={handleClose} />
            </header>
          </div>

          <div className="suarte-plus-popup__body">
            {configError && (
              <p className="suarte-plus-popup__error" role="alert">
                {configError}
              </p>
            )}

            {!showPaymentDetails ? (
              <>
                {planSelection}
                <div className="suarte-plus-popup__benefits-section">
                  <p className="suarte-plus-popup__benefits-heading">Includes</p>
                  <ul className="suarte-plus-popup__benefits" aria-label="Suarte Plus benefits">
                    {previewBenefitsList}
                  </ul>
                </div>
                {hasAdditionalBenefits && (
                  <button
                    type="button"
                    className="suarte-plus-popup__benefits-action"
                    onClick={() => setShowAllBenefits(true)}
                  >
                  See all benefits
                  </button>
                )}
              </>
            ) : (
              <section className="suarte-plus-popup__payment-section">
                {planSelection}
                <div className="suarte-plus-popup__totals">
                  <div>
                    <span>Subtotal</span>
                    <strong>{formatAmount(planAmount, 2)}</strong>
                  </div>
                  <div>
                    <span>{`VAT (${vatRateLabel})`}</span>
                    <strong>{formatAmount(resolvedVatAmount, 2)}</strong>
                  </div>
                  <div>
                    <span>Total</span>
                    <strong>{formatAmount(resolvedTotal, 2)}</strong>
                  </div>
                </div>
                {vatSummaryLoading && (
                  <p className="suarte-plus-popup__payment-feedback" role="status">Calculating VAT...</p>
                )}
                {vatSummaryError && (
                  <p className="suarte-plus-popup__payment-feedback suarte-plus-popup__payment-feedback--error" role="alert">
                    {vatSummaryError}
                  </p>
                )}
                <div className="suarte-plus-popup__payment-methods">
                  <div className="suarte-plus-popup__payment-methods-copy">
                    <CreditCardIcon className="suarte-plus-popup__payment-methods-icon" />
                    <div>
                      <p className="suarte-plus-popup__payment-methods-title">Payment method</p>
                      {!loadingPaymentMethods && !paymentMethods.length && (
                        <p className="suarte-plus-popup__payment-methods-message">
                          {paymentMethodsError || "No payment methods saved yet."}
                        </p>
                      )}
                    </div>
                  </div>
                  {loadingPaymentMethods ? (
                    <p className="suarte-plus-popup__payment-feedback" role="status">Loading payment methods...</p>
                  ) : paymentMethods.length > 0 ? (
                    <div className="suarte-plus-popup__payment-methods-list">
                      {paymentMethods.map((method) => {
                        const isActive = method.id === selectedPaymentMethodId;
                        const detailLine = method.details ?? `${method.brand} **** ${method.last4}`;
                        return (
                          <button
                            type="button"
                            key={method.id}
                            className={isActive ? "active" : ""}
                            onClick={() => setSelectedPaymentMethodId(method.id)}
                            aria-pressed={isActive}
                          >
                            <span>{detailLine}</span>
                            {method.isDefault && <small>Default</small>}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  <ContinueButton
                    className="suarte-plus-popup__payment-add"
                    onClick={handleAddPaymentMethod}
                    loading={addingPaymentMethod}
                  >
                  Add payment method
                  </ContinueButton>
                </div>
                <BillingDetailsCard
                  className="suarte-plus-popup__billing"
                  billingAddress={billingAddress}
                  billingInfo={billingInfo}
                  onSaved={({ billingAddress: nextAddress, billingInfo: nextInfo }) => {
                    setBillingAddress(nextAddress ?? null);
                    setBillingInfo(nextInfo ?? null);
                  }}
                  triggerOnly
                />
                {subscriptionError && (
                  <p className="suarte-plus-popup__payment-feedback suarte-plus-popup__payment-feedback--error" role="alert">
                    {subscriptionError}
                  </p>
                )}
              </section>
            )}
          </div>
        </div>

        <footer className="suarte-plus-popup__footer">
          <ContinueButton
            onClick={handlePrimaryAction}
            loading={processingSubscription}
            disabled={processingSubscription || (showPaymentDetails && !selectedPaymentMethod)}
            title={PAYMENT_INTENT_NOTE}
          >
            {showPaymentDetails
              ? `${ctaLabel} - ${checkoutPriceLabel}`
              : `${ctaLabel}`}
          </ContinueButton>
        </footer>
      </GenericPopup>

      <GenericPopup
        open={open && showAllBenefits}
        className="suarte-plus-popup__benefits-card"
        backgroundClassName="suarte-plus-popup__benefits-backdrop"
        onBackgroundClick={() => setShowAllBenefits(false)}
      >
        <header className="suarte-plus-popup__header element-non-selectable">
          <h2 className="suarte-plus-popup__title">Suarte Plus benefits</h2>
          <XIcon className="suarte-plus-popup__close" onClick={() => setShowAllBenefits(false)} />
        </header>
        <div className="suarte-plus-popup__benefits-section">
          <p className="suarte-plus-popup__benefits-heading">Includes</p>
          <ul className="suarte-plus-popup__benefits suarte-plus-popup__benefits--all" aria-label="All Suarte Plus benefits">
            {normalizedBenefitsList}
          </ul>
        </div>
      </GenericPopup>
    </>
  );
}

SuartePlusPopup.propTypes = {
  actionTitle: PropTypes.node,
  benefits: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      title: PropTypes.string,
    })
  ),
  highlightedBenefit: PropTypes.string,
  ctaLabel: PropTypes.string,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  open: PropTypes.bool,
};
